import { ApiError, NetworkError, type ApiRequestOptions } from './types';
import type { SessionToken } from '../session';
// Import the file directly (not the session barrel) to avoid pulling the
// React-bound SessionProvider into the api module and any import cycle.
import { triggerSessionExpired } from '../session/sessionExpiry';

interface ClientConfig {
  baseUrl: string;
  getToken: () => SessionToken | null;
  /** Anonymous embed mode: when set (and no token), iframe GETs are
   *  rewritten to the public/ twins with `embed_key` appended. */
  getEmbedKey?: () => string | null;
}

export interface ApiClient {
  request<T = unknown>(path: string, opts?: ApiRequestOptions): Promise<T>;
}

export function createApiClient({ baseUrl, getToken, getEmbedKey }: ClientConfig): ApiClient {
  // Coalesce identical GETs that are in flight at the SAME instant into a
  // single network request — e.g. React StrictMode double-mounting an
  // effect in dev, or two components asking for the same resource at once.
  // It only ever holds IN-FLIGHT promises (cleared the moment one settles),
  // so it dedupes, it does NOT cache: a GET issued after the previous one
  // resolved still hits the network and sees fresh data.
  const inFlightGets = new Map<string, Promise<unknown>>();

  async function doFetch(path: string, opts: ApiRequestOptions): Promise<Response> {
    const method = (opts.method ?? 'GET').toUpperCase();
    const token = getToken();
    // Anonymous embed mode: no token + embed key → reroute iframe GETs to
    // their public/ twins (operator-scoped catalog) with the key appended.
    // Non-GETs go through unchanged (they 401 — callers gate on auth first).
    // With a token, behavior is byte-identical to the authed flow.
    let effectivePath = path;
    if (!token) {
      const embedKey = getEmbedKey?.() ?? null;
      const IFRAME_PREFIX = '/loot_machine/api/game_platform/iframe/v1/';
      if (
        embedKey &&
        method === 'GET' &&
        path.startsWith(IFRAME_PREFIX) &&
        !path.startsWith(`${IFRAME_PREFIX}public/`)
      ) {
        const [p = '', q] = path.split('?');
        const rest = p.slice(IFRAME_PREFIX.length);
        const qs = new URLSearchParams(q ?? '');
        qs.set('embed_key', embedKey);
        effectivePath = `${IFRAME_PREFIX}public/${rest}?${qs.toString()}`;
      }
    }
    const url = baseUrl + effectivePath;
    const headers = new Headers({ 'Content-Type': 'application/json' });
    if (token && !opts.anonymous) headers.set('X-Platform-Session', token);
    // Caller-supplied headers (e.g. X-Idempotency-Key) layer on top of the
    // defaults without clobbering Content-Type / the session token.
    if (opts.headers) {
      for (const [k, v] of Object.entries(opts.headers)) headers.set(k, v);
    }

    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers,
    };
    if (opts.signal) init.signal = opts.signal;
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

    try {
      return await fetch(url, init);
    } catch (err) {
      throw new NetworkError(err instanceof Error ? err.message : 'unknown');
    }
  }

  async function parseError(res: Response): Promise<ApiError> {
    let code: string | undefined;
    let message = `HTTP ${res.status}`;
    let data: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as {
        code?: string;
        message?: string;
        error_detail?: { code?: string; message?: string };
      };
      // Keep the whole parsed body so endpoint-specific error fields survive
      // (e.g. the withdraw 403 gate's `setup_account_url` / `link`).
      data = body as Record<string, unknown>;
      // game_platform iframe errors carry code/message under `error_detail`
      // (`{ error: 1, error_detail: { code, message } }`); other endpoints use
      // top-level `code`/`message`. Read both so the error code is never lost.
      code = body.code ?? body.error_detail?.code;
      const detailMessage = body.message ?? body.error_detail?.message;
      if (detailMessage) message = detailMessage;
    } catch {
      // non-JSON body
    }
    return new ApiError(res.status, code, message, data);
  }

  async function runRequest<T>(
    path: string,
    opts: ApiRequestOptions,
    method: string,
  ): Promise<T> {
    let res = await doFetch(path, opts);
    // Retry once on a transient 5xx — but ONLY for idempotent methods.
    // Auto-retrying a POST/PATCH/DELETE risks a DOUBLE side-effect (a second
    // purchase / sell / order) when the server actually processed the first
    // call and only the response was lost.
    if (res.status >= 500 && (method === 'GET' || method === 'HEAD')) {
      await new Promise((r) => setTimeout(r, 300));
      res = await doFetch(path, opts);
    }
    if (!res.ok) {
      // A 401 from ANY call (startup or mid-gameplay) means the platform
      // session is gone. Drive the global expired view once, then still
      // throw so per-call-site catches keep running.
      if (res.status === 401) triggerSessionExpired();
      throw await parseError(res);
    }
    const contentType = res.headers.get('content-type') ?? '';
    // Match application/json AND application/vnd.api+json, ld+json, etc.
    if (/\bjson\b/i.test(contentType)) return (await res.json()) as T;
    return undefined as T;
  }

  return {
    request<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
      const method = (opts.method ?? 'GET').toUpperCase();
      // Only GETs are deduped — they're idempotent and safe to share. A
      // mutating request must always be sent (each is a distinct action).
      if (method !== 'GET') return runRequest<T>(path, opts, method);

      const existing = inFlightGets.get(path) as Promise<T> | undefined;
      if (existing) return existing;

      const p = runRequest<T>(path, opts, method);
      inFlightGets.set(path, p);
      void p
        .catch(() => undefined)
        .finally(() => {
          if (inFlightGets.get(path) === p) inFlightGets.delete(path);
        });
      return p;
    },
  };
}
